import { httpsCallable } from 'firebase/functions';
import { FirebaseError } from 'firebase/app';

import { getFirebaseFunctions } from '@/core/services/authentication/firebase-app';

export type FirebaseCallableName = string;

/**
 * Wrapper genérico para Cloud Functions (HTTPS Callable).
 *
 * Centraliza:
 * - obtenção da instância do SDK (`getFirebaseFunctions`, com emulador quando habilitado)
 * - cache do callable por nome (evita recriar handlers)
 * - tipagem genérica de request/response
 */
const callablesCache = new Map<FirebaseCallableName, unknown>();

function getOrCreateCallable<TRequest, TResponse>(
  name: FirebaseCallableName
): ReturnType<typeof httpsCallable<TRequest, TResponse>> {
  const cached = callablesCache.get(name) as
    | ReturnType<typeof httpsCallable<TRequest, TResponse>>
    | undefined;
  if (cached) return cached;
  const created = httpsCallable<TRequest, TResponse>(getFirebaseFunctions(), name);
  callablesCache.set(name, created);
  return created;
}

export async function callFirebaseFunction<TRequest, TResponse>(
  name: FirebaseCallableName,
  data: TRequest
): Promise<TResponse> {
  const result = await getOrCreateCallable<TRequest, TResponse>(name)(data);
  return result.data;
}

/** `true` quando o erro veio de uma Callable Function (`functions/...`). */
export function isFirebaseFunctionsError(error: unknown): error is FirebaseError {
  return error instanceof FirebaseError && error.code.startsWith('functions/');
}

